import { API_BASE_URL, HAS_API_URL } from '../config/api';

/**
 * @param {import('firebase/auth').User} user
 * @param {string} path
 * @param {RequestInit} [options]
 */
async function adminFetch(user, path, options = {}) {
  if (!user) {
    return { ok: false, error: 'You must be signed in.' };
  }
  if (!HAS_API_URL) {
    return {
      ok: false,
      error: 'The app is not configured to reach the server. Please contact support.',
    };
  }

  try {
    const idToken = await user.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/admin${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${idToken}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });

    let body = null;
    const ct = res.headers.get('Content-Type') || '';
    if (ct.includes('application/json')) {
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
    }

    if (res.ok) {
      return { ok: true, data: body };
    }

    const serverMsg = typeof body?.error === 'string' ? body.error : null;
    if (res.status === 401) {
      return { ok: false, error: 'Your session could not be verified. Please sign out and sign in again.' };
    }
    if (res.status === 403) {
      return { ok: false, error: serverMsg || 'You do not have admin access.' };
    }
    return { ok: false, error: serverMsg || 'Something went wrong. Please try again.' };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Network error. Please try again.',
    };
  }
}

/** @param {import('firebase/auth').User} user */
export async function getAdminUsers(user) {
  return adminFetch(user, '/users');
}

/** @param {import('firebase/auth').User} user @param {string} uid */
export async function getAdminUserProjects(user, uid) {
  return adminFetch(user, `/users/${encodeURIComponent(uid)}/projects`);
}

/** @param {import('firebase/auth').User} user @param {string} uid @param {number} credits */
export async function updateAdminUserCredits(user, uid, credits) {
  return adminFetch(user, `/users/${encodeURIComponent(uid)}/credits`, {
    method: 'PATCH',
    body: JSON.stringify({ credits }),
  });
}

/** @param {import('firebase/auth').User} user @param {string} uid */
export async function deleteAdminUser(user, uid) {
  return adminFetch(user, `/users/${encodeURIComponent(uid)}`, {
    method: 'DELETE',
  });
}

/** @param {import('firebase/auth').User} user */
export async function previewIdleZeroCreditCleanup(user) {
  return adminFetch(user, '/cleanup/idle-zero-credit-projects/preview');
}

/** @param {import('firebase/auth').User} user */
export async function runIdleZeroCreditCleanup(user) {
  return adminFetch(user, '/cleanup/idle-zero-credit-projects/run', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'DELETE_IDLE_ZERO_CREDIT_PROJECTS' }),
  });
}
